import { useContext } from "react";
import reactStringReplace from "react-string-replace";

import { Word, Link, Explanation, Desc, Example } from './types/dict';
import { dictDataContext } from "./App";

import './App.scss';
import './pagenate.scss'

import {typeClass, sentenceToPron} from "./funcs"

import { FaAngleRight } from "react-icons/fa6";
import { FaCaretRight } from "react-icons/fa6";
import { FaRegCopy } from "react-icons/fa6";
import { IoIosArrowUp } from "react-icons/io";

export const Entry = (props: {entry: Word, example: Example[]}) => {
    const e = props.entry
    const example = props.example.filter(ex => ex.words.some(w => w.link == e.word)) //この単語が含まれる例文のみを抽出

    return (
        <div className="content">
            <div className="word">
                <div>
                <span className="phun phunFont">{e.word}</span>
                <span className="subWord">
                    <span className="trans serif">{e.word}</span>
                    <span className="latinPron">⟨
                        <span className="serif">{e.latinPron}</span>
                    ⟩</span>
                    <span className="pron">⟨
                        <span className="serif">{e.pron}</span>
                    ⟩</span>
                    <span className="weqoPron phunFont">⟨{e.weqo}⟩</span>
                </span>
                </div>
                <span className="rightWordBox">
                    { e.num != "-" && <>
                        <span className="num">{"#" + e.num}</span>
                        <a href={`https://qo.kaeru2193.net/chara/${e.num}`} target="_blank" className="charaDictIcon iconButton phunFont" title="雰字典で見る">
                            <div>字</div>
                        </a>
                    </> }
                    <span className="wordCopy iconButton" title="リンクをコピー" onClick={() => navigator.clipboard.writeText(
                            `${window.location.origin}/?type=id&rule=perfect&text=${e.id}`
                    )}><FaRegCopy/></span>
                </span>
            </div>
            <div className="wordDesc">
                <Mean data={e.mean}/>
                <Append data={e.append}/>
                {e.link? <WordLink data={e.link}/>: <></>}
            </div>
            <div className="wordInfo">
                <details>
                    <summary className="expandInfo">単語情報</summary>
                    {e.word.length >= 2 && <>
                        <h3>この語の雰字</h3>
                        <CharaInfo word={e.word} pron={e.pron}/>
                    </>}
                    {e.createdAt && <>
                        <h3>記載日</h3>
                        {e.createdAt.replace(/-/g, "/")}
                    </>}
                    {e.id && <>
                        <h3>単語ID</h3>
                        {e.id}
                    </>}
                </details>
                {example.length > 0 && <ExampleBox example={example} word={e.word}/>}
            </div>
        </div>
    )
}

const Mean = (props: {data: Word["mean"]}) => {
    const descArr = props.data.map((a, idx) => 
        <EntryWrapper
        type={a.type}
        exp={a.explanation}
        class={typeClass[a.type]}
        func={MeanDesc}
        key={idx}
        />
    )
    return <div className='meanExp'>{descArr}</div>
}

const Append = (props: {data: Word["append"]}) => {
    const descArr = props.data.map((a, idx) => 
        <EntryWrapper
        type={a.type}
        exp={a.explanation}
        key={idx}
        func={AppendDesc}
        />
    )
    return <>{descArr}</>
}

const WordLink = (props: {data: Link[]}) => {
    const descArr = props.data.map((a, idx) => 
        <EntryWrapper
        type={a.type}
        exp={a.explanation}
        key={idx}
        func={LinkDesc}
        />
    )
    return <>{descArr}</>
}

const EntryWrapper = (props:
    {
        type: string,
        class?: string,
        exp: Explanation[] | string[],
        func: (props: any) => JSX.Element
    }) => {
        return (
        <div className="speech">
            <span className={`type ${props.class}`}>{props.type}</span>
            <div className="text"><props.func exp={props.exp}/></div>
        </div> //一つ一つの表示形式を指定するためpropsで関数を受け取る
        )
}

const toLink = (text: string) => {
    return reactStringReplace(text, /\[(.*?)\]/g, (match) => 
        (<a href={`/?rule=perfect&text=${match}`} target="_blank">
            <span className="serif">{match}</span>
        </a>)
    )
}

const prepositionStructure = (text: string, dict: Word[]) => { //格組を表示
    const grammar = ["S", "V", "O", "A", "N"]
    return reactStringReplace(text, /\[(.*?)\]\s*/g, (match) => {
        const structure = match.split(" ")
        return <span className="prepositionStructure">[
            {structure.map((s, idx) => {
                const isPreposition = dict.some(w => w.word == s)
                const isGrammarMarker = grammar.includes(s)
                return <span key={idx}>
                    {isPreposition
                        ? <a className="element serif" href={`/?rule=perfect&text=${s}`} target="_blank">
                            {s}
                        </a>
                    :isGrammarMarker
                        ? <span className="element serif">{s}</span>
                        : s
                    }
                </span>
            })}
        ]</span>
    })
}

const MeanDesc = (props: any) => {
    const dictData = useContext(dictDataContext)
    const nums = ["一","二","三","四","五","六","七","八","九","十"]
    const exp = props.exp

    const expData = 
        exp.map((r: Explanation, idx: number) => //rには品詞内のエントリが入る
        <div className='expRow' key={idx}>
            {exp.length > 1? //語義が二つ以上ある場合、先頭に番号を表示
                <span className='expNumContainer'>
                <span className='expNum'>{nums[idx]}</span>
                </span>
                : <></> }

            <div className='expTexts'>
                <span>
                    <span>
                        {r.tag.map(t => <span className='expTag' key={t}>{`〈${t}〉`}</span>)}
                    </span>
                    {r.pron && <span>
                        <span className='pronVariant'>（<span className="serif">{r.pron}</span> と読み）</span>
                    </span>}
                    {prepositionStructure(r.translate, dictData)}
                </span>
                <div className='expAppend'>
                    {r.meaning?
                    <span className='expMean'>
                        <span className='expMeanIcon'><FaAngleRight /></span>
                        <span className='expMeanText'>
                        {toLink(r.meaning)}
                        </span>
                    </span>
                    : <></>}
                </div>
            </div>
        </div>
        )

    return <>{expData}</>
}

const AppendDesc = (props: any) => {
    const expData: string = props.exp[0].translate

    const linkProcessed = toLink(expData)
    return <>{linkProcessed}</>
}

const LinkDesc = (props: any) => {
    return (
        <span>
        {
        props.exp.map((l: string, idx: number) => {
            const linkPart = l.endsWith("*") ?l.slice(0, -1) :l //リンクがアスタリスクで終わる場合、取り除く
            const linkElem = <a className="serif" href={`/?rule=perfect&text=${linkPart}`} target="_blank">{linkPart}</a> //リンク作成
            const linkText = <>{linkElem}{l.endsWith("*") ?"*" :""}</> //アスタリスクがあればテキストとして付与
            return <span key={idx}>
                {props.exp.length - 1 > idx ? <>{linkText}, </> : linkText}
            </span> //末尾の要素以外は読点を付ける
        })
        }
        </span>
    )
}

const CharaInfo = (props: {word: string, pron: string}) => {
    const dictData = useContext(dictDataContext)
    const charas = props.word.split("")
    const prons = props.pron.split(" ")

    return <div className="charaInfo">
        {charas.map((chara, idx) => {
            const dictEntry = dictData.find(e => e.word == chara)
            const translations = dictEntry?.mean.map(m => m.explanation.map(exp => 
                exp.translate
                    .replace(/^\s*\[.*?\]\s*/, "") //格組を取り除く
                    .replace(/[（）]/g, "") //訳語の括弧は取り払う
                    .split("。")[0]
            )).flat()
            const composedTrans = translations
                ? Array.from(new Set(translations)).reduce((pre, now, idx) => //一旦setにして重複を排除
                    <>{idx > 0 ?<>{pre}；</> :pre}<span>{now}</span></>, <></>)
                : <></>

            return (
            <div className="charaBox" key={idx}>
                <span className="pron serif">{prons[idx]}</span>
                <span className="phun phunFont">
                    <a href={`/?rule=perfect&text=${chara}`} target="_blank">{chara}</a>
                </span>
                <div className="transBox">
                    <div className="translation">{composedTrans}</div>
                </div>
            </div>)
        })}
    </div>
}

const ExampleBox = (props: {example: Example[], word: string}) => {
    const example = props.example
    const [firstExample, moreExample] = [example.slice(0, 3), example.slice(3)] //最初の3つを切り離して常時表示
    return <>
        <h3 className="exampleTitle">例文 ({example.length})</h3>
        <div className="example">
            <ExampleSentences data={firstExample} word={props.word}/>
            {moreExample.length > 0 &&
                <details>
                    <summary className="exampleMore">
                        <span className="moreIcon"><IoIosArrowUp /></span>全ての例文を表示
                    </summary>
                    <ExampleSentences data={moreExample} word={props.word}/>
                </details>}
        </div>
    </>
        
}

const ExampleSentences = (props: {data: Example[], word: string}) => {
    const examples = props.data
    const highlight = props.word
    const dict = useContext(dictDataContext) //発音変換用
    return <>
        {examples.map((s, idx) => 
            {
            const highlightID = s.words.findIndex(w => w.link == highlight)

            const flattened = s.words.map((word, idx) => //全ての単語を同じ階層に取り出して平たくする
                word.parts.map(part => {
                    return {part, idx}
                }
            )).flat()

            const sentencePron = sentenceToPron(flattened.map(p => p.part), dict)
            const sentenceIndexes = flattened.map(p => p.idx)

            return (<div className='exampleSentence' key={idx}>
                <div className='sentencePhun'>
                    <span className='phun phunFont'>
                        {s.words.map((w, idx) =>
                            <SentenceWord word={w.parts.map(p => p.text).join("")} link={w.link} highlight={idx == highlightID}/>
                        )}
                    </span>
                    <span className='kanji serif'>
                        {s.words.map((w, idx) =>
                            <span className={idx == highlightID ?"highlightWord" :""}>{w.parts.map(p => p.text).join("")}</span>
                        )}
                    </span>
                </div>
                <div className="sentencePron">
                    <span className='sentencePNPron serif'>
                        {sentencePron.map((p, idx) =>
                            <span className={sentenceIndexes[idx] == highlightID ?"highlightWord" :""}>{p}</span>
                        )}
                    </span>
                </div>
                <div className='sentenceJA'>
                    <span className='sentenceIcon'><FaCaretRight /></span>
                    <span className='ja'>{s.ja}</span>
                    {s.supplement && <span className="supplement">({s.supplement})</span>}
                </div>
            </div>)}
        )}
    </>
}

const SentenceWord = (props: {word: string, link: string, highlight: boolean}) => {
    const word = props.word

    const marks = ["、", "。", "「", "」", "〈", "〉", "！", "？"]
    const link = (marks.includes(word[0]) || props.highlight)
        ? <>{word}</>
        : <a href={`/?rule=perfect&text=${props.link}`} target="_blank">{word}</a>

    return <span className={`sentenceWord ${props.highlight && "highlightWord"}`}>
        {link}
    </span>
}